"use client";

import { useEffect, useMemo } from "react";
import type { Messages } from "@lingui/core";
import { LinguiClientProvider } from "@marketing-sites/l10n/providers/lingui-client-provider";
import { LocalizeHrefProvider } from "@marketing-sites/l10n/providers/localize-href-context";
import { createLocaleProvider } from "@marketing-sites/l10n/providers/locale-provider";
import { writeLocalePreference } from "@marketing-sites/l10n/preference/cookie";
import { hasFunctionalConsent } from "@marketing-sites/marketo/consent";

import { localeConfig, LOCALE_UI_ENABLED } from "./config";
import {
  localizeCrossSiteHref,
  localizeHref,
  localizeSignupHref,
} from "./href";

const { LocaleProvider, useLocalePrefix, useLogicalPathname } =
  createLocaleProvider(localeConfig);

export { useLocalePrefix, useLogicalPathname };

/**
 * The three localizers chained. They cover disjoint sets of hrefs —
 * `localizeHref` declines anything cross-domain, `localizeCrossSiteHref` only
 * touches fin.ai, and `localizeSignupHref` only the signup URLs both decline —
 * so composing them keeps a reader in their locale whichever site a link points
 * at, and each is individually idempotent.
 */
function localizeAnyHref(href: string, prefix: string): string {
  return localizeSignupHref(
    localizeCrossSiteHref(localizeHref(href, prefix), prefix),
    prefix,
  );
}

/**
 * Returns a localizer bound to the active prefix, for links rendered in client
 * components. Safe on any href — see `localizeHref`.
 */
export function useLocalizedHref(): (href: string) => string {
  const prefix = useLocalePrefix();
  return useMemo(
    () => (href: string) => localizeAnyHref(href, prefix),
    [prefix],
  );
}

export function LocalePreferenceWriter({ prefix }: { prefix: string }) {
  useEffect(() => {
    if (prefix !== localeConfig.DEFAULT_PREFIX) {
      writeLocalePreference(localeConfig, prefix, {
        hasConsent: hasFunctionalConsent,
      });
    }
  }, [prefix]);

  return null;
}

// Client boundary mounted by [locale]/layout.tsx: it publishes the active
// prefix for client hooks and activates the locale's Lingui catalog. Compiled
// catalogs are plain JSON — ICU plurals included — so the server→client
// messages payload stays serialisable. Pinned by messages.test.ts.
export function LocaleGate({
  prefix,
  locale,
  messages,
  children,
}: {
  prefix: string;
  locale: string;
  messages: Messages;
  children: React.ReactNode;
}) {
  const localize = useMemo(
    () => (href: string) => localizeAnyHref(href, prefix),
    [prefix],
  );

  return (
    <LocaleProvider prefix={prefix}>
      <LocalizeHrefProvider localizeHref={localize}>
        {LOCALE_UI_ENABLED && <LocalePreferenceWriter prefix={prefix} />}
        <LinguiClientProvider
          locale={locale}
          messages={messages}
          sourceLocale={localeConfig.sourceLocale}
          pseudoLocale={localeConfig.PSEUDO_LOCALE}
        >
          {children}
        </LinguiClientProvider>
      </LocalizeHrefProvider>
    </LocaleProvider>
  );
}
